import { FC, useCallback, useEffect, useState } from 'react';

import cn from 'clsx';

import InputText, { InputTextProps } from '../InputText';

import Modal from './Modal';
import { DatePickerInterval, generateDates } from './utils';

interface DatePickerProps extends Omit<InputTextProps, 'onChange' | 'value'> {
  onChange?: (value: Date) => void;
  title?: string;
  interval?: DatePickerInterval;
  focus?: 'start' | 'end';
  start: Date;
  end: Date;
  value?: Date;
  dateFormat?: string;
}

const DatePicker: FC<DatePickerProps> = ({
  onChange,
  title,
  className,
  interval = 'day',
  focus = 'end',
  start,
  end,
  value: propValue,
  children,
  dateFormat,
  ...inputProps
}) => {
  const [dates, setDates] = useState<Date[]>(
    generateDates(start, end, interval)
  );

  useEffect(
    () => setDates(generateDates(start, end, interval)),
    [start, end, interval]
  );

  const [open, setOpen] = useState<boolean>(false);
  const [value, setValue] = useState<Date | undefined>(propValue);
  const displayValue = propValue === undefined ? value : propValue;

  const showModal = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const hideModal = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const _onChange = useCallback(
    (date: Date) => {
      setValue(date);
      onChange?.(date);
      hideModal();
    },
    [hideModal, onChange]
  );

  const rootCn = cn(
    'cursor-pointer select-none pointer-events-none',
    className
  );

  return (
    <>
      <div onClick={showModal}>
        {children || (
          <InputText
            className={rootCn}
            type="text"
            value={displayValue?.getFullYear?.()}
            {...inputProps}
          />
        )}
      </div>
      <Modal
        title={title}
        interval={interval}
        open={open}
        onClose={hideModal}
        onClick={(v) => {
          if (propValue) {
            onChange?.(v);
          } else {
            _onChange(v);
          }
          hideModal();
        }}
        value={displayValue}
        dateFormat={dateFormat}
        dates={dates}
      />
    </>
  );
};

export default DatePicker;
